import { useState } from "react";
import { Loader2, ExternalLink, Receipt, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import { useConnectStatus } from "./StripeConnectSettings";
import { currencySymbol, formatCurrency } from "../contexts/FxContext";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";
import { calculatePlatformFee, formatEffectiveFeePercent } from "../lib/platformFees";

function parseApiError(err) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "object" && detail?.message) return detail.message;
    if (typeof detail === "string") return detail;
    return err.message;
}

function isConnectRequired(err) {
    const detail = err.response?.data?.detail;
    return typeof detail === "object" && detail?.code === "connect_required";
}

export function StripeInvoiceDialog({ pool, open, onClose, onCreated }) {
    const [amount, setAmount] = useState("");
    const [clientName, setClientName] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const { status: connectStatus, loading: connectLoading, ready: connectReady } = useConnectStatus(pool?.org_id);

    if (!open) return null;

    const currency = pool?.currency || DEFAULT_CURRENCY;
    const symbol = currencySymbol(currency);

    const submit = async () => {
        const num = parseFloat(amount || "0");
        if (!num || num <= 0) { toast.error("Enter a positive amount"); return; }
        try {
            setBusy(true);
            const origin = window.location.origin;
            const { data } = await api.post("/payments/checkout", {
                pool_id: pool.id, amount: num,
                client_name: clientName || "Client",
                description, origin_url: origin,
            });
            toast.success("Stripe Checkout ready", { description: "Opening in a new tab…" });
            window.open(data.url, "_blank", "noopener,noreferrer");
            onCreated?.(data);
            onClose();
        } catch (err) {
            if (isConnectRequired(err)) {
                toast.error("Connect Stripe first", {
                    description: "Set up Stripe Connect in Settings → Connections before billing clients.",
                    action: { label: "Settings", onClick: () => window.location.assign("/settings?tab=connections") },
                });
            } else {
                toast.error(parseApiError(err));
            }
        } finally { setBusy(false); }
    };

    const amountPreview = (() => {
        const num = parseFloat(amount || "0");
        if (!num || num <= 0) return null;
        const platformFee = calculatePlatformFee(num);
        const net = Math.round((num - platformFee) * 100) / 100;
        return { gross: num, platformFee, net };
    })();

    return (
        <Dialog onClose={onClose} title="Bill a client via Stripe" subtitle="Client pays → your Stripe account receives the net amount. FairShare tracks the pool split." testid="stripe-invoice-dialog">
            <div className="space-y-3">
                {!connectLoading && !connectReady && (
                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 flex items-start gap-2.5" data-testid="connect-required-banner">
                        <Link2 className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                        <div className="text-xs text-white/65 space-y-1.5">
                            <p>Connect Stripe before billing clients — payments deposit to your account, not FairShare.</p>
                            <Link to="/settings?tab=connections" className="text-[#00BF0D] hover:underline inline-flex items-center gap-1">
                                Set up Stripe Connect →
                            </Link>
                        </div>
                    </div>
                )}
                <div>
                    <label className="label-eyebrow">Client name</label>
                    <input
                        value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Inc."
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="stripe-client-name"
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Amount ({currency})</label>
                    <div className="mt-1.5 relative">
                        <span className="absolute inset-y-0 left-3 flex items-center text-white/45 font-mono">{symbol}</span>
                        <input
                            type="text" inputMode="decimal" value={amount}
                            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                            className={`input-dark w-full rounded-md pr-3 py-2 text-sm font-mono ${symbol.length > 1 ? "pl-10" : "pl-7"}`}
                            data-testid="stripe-amount"
                        />
                    </div>
                    {amountPreview != null && (
                        <div className="mt-1.5 space-y-1 text-[11px] text-white/40 font-mono">
                            <p>{formatCurrency(amountPreview.gross, currency, { decimals: 2 })} checkout total</p>
                            <p>
                                Platform fee (tiered, eff. {formatEffectiveFeePercent(amountPreview.gross)}):{" "}
                                {formatCurrency(amountPreview.platformFee, currency, { decimals: 2 })}
                            </p>
                            <p className="text-white/55">
                                Pool revenue (net): {formatCurrency(amountPreview.net, currency, { decimals: 2 })}
                            </p>
                        </div>
                    )}
                </div>
                <div>
                    <label className="label-eyebrow">Description (optional)</label>
                    <input
                        value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Q1 brand identity engagement"
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="stripe-description"
                    />
                </div>
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.025] p-3 flex items-start gap-2.5">
                    <Receipt className="h-4 w-4 text-[#00BF0D] mt-0.5 shrink-0" />
                    <p className="text-xs text-white/55">
                        After payment, FairShare records the net amount as revenue in <span className="text-white">{pool?.name}</span> and splits it among members.
                        {connectStatus?.ready && " Funds settle to your connected Stripe account."}
                        {" "}See{" "}
                        <Link to="/terms" className="text-[#00BF0D] hover:underline">Terms</Link> for the tiered fee schedule.
                    </p>
                </div>
            </div>
            <DialogFooter>
                <button onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} disabled={busy || connectLoading || !connectReady} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="stripe-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                    {busy ? "Opening…" : connectReady ? "Open Stripe Checkout" : "Connect Stripe first"}
                </button>
            </DialogFooter>
        </Dialog>
    );
}
